import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import {
  AccountData,
  PageState,
} from "@pages/complete-account/complete-account-types";

/**
 * @returns Whether the passed text input value has at least one non-space char
 * @param value Value to validate
 */
export function validateTextInput(value?: string): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * @returns Whether each entry of the account data is valid
 * @param accountData Account data object
 */
export function isAccountComplete(accountData: AccountData): boolean {
  return Object.values(accountData).every(validateTextInput);
}

/**
 * @returns The title to display in the complete account page
 * @param state State of the complete account page
 */
export function getTitle(state: PageState): string {
  switch (state) {
    case "loading":
    case "loadedIncomplete":
    case "loggedOut":
      return "Complete account";
    case "loadedComplete":
    case "updated":
      return "Account completed";
  }
}

/**
 * @returns The subtitle to display in the complete account page
 * @param state State of the complete account page
 */
export function getSubtitle(state: PageState): string {
  switch (state) {
    case "loading":
      return "Loading account information...";
    case "loadedIncomplete":
      return "Please provide the following information to complete your account.";
    case "loadedComplete":
      return "Your account information is already completed.";
    case "loggedOut":
      return "You are logged out. Log in again to complete your account.";
    case "updated":
      return "Account information completed successfully.";
  }
}

/**
 * Fetches the user data and returns the required account data.
 * As there is a need to access "phone", "companyName" and "jobTitle" properties, getLoggedInUser (v3/users/me) cannot be used
 * @returns The required account data.
 * @throws {Error} if it fails to fetch the user data.
 */
export async function fetchAccountData(): Promise<AccountData> {
  const data = await faroCoreApiClient.users.getLoggedInUserV1();
  return {
    mailAddress: data.mailAddress,
    name: data.name,
    lastName: data.lastName,
    phone: data.phone,
    companyName: data.companyName,
    jobTitle: data.jobTitle,
  };
}
